import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Mail, ChevronDown } from 'lucide-react';
import Logo from './Logo';
import { navigation } from '../../data/navigation';

const MobileNavItem = ({ item, onClose }) => {
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState(false);
  const active = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));

  if (!item.dropdown) {
    return (
      <Link
        to={item.href}
        onClick={onClose}
        className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
          active ? 'bg-cygnet-dark text-white' : 'text-gray-700 hover:bg-gray-50 hover:text-cygnet-dark'
        }`}
      >
        {item.name}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium transition-colors ${
          active ? 'bg-cygnet-dark text-white' : 'text-gray-700 hover:bg-gray-50 hover:text-cygnet-dark'
        }`}
      >
        {item.name}
        <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className={`h-4 w-4 ${active ? 'text-white/70' : 'text-gray-400'}`} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-1 ml-4 pl-4 border-l-2 border-cygnet-dark/20 space-y-0.5 pb-2">
              {item.dropdown.map((child) => (
                <Link
                  key={child.href}
                  to={child.href}
                  onClick={onClose}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-cygnet-dark hover:bg-cygnet-dark/5 transition-colors"
                >
                  <span className="w-1 h-1 rounded-full bg-cygnet-dark/30 shrink-0" />
                  {child.name}
                </Link>
              ))}
              <Link
                to={item.href}
                onClick={onClose}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-cygnet-dark hover:underline"
              >
                View all {item.name} →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileNavMenu = ({ open, onClose }) => (
  <>
    {/* Backdrop */}
    <div
      className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={onClose}
      aria-hidden="true"
    />

    {/* Drawer */}
    <div
      className={`fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${open ? 'translate-x-0' : 'translate-x-full'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <Link to="/" onClick={onClose} aria-label="Home">
          <Logo />
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto px-4 py-4">
        <ul className="space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              <MobileNavItem item={item} onClose={onClose} />
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer CTA */}
      <div className="border-t border-gray-100 px-6 py-6 space-y-3 bg-gray-50 shrink-0">
        <Link
          to="/contact"
          onClick={onClose}
          className="flex items-center justify-center w-full rounded-xl bg-cygnet-dark px-5 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-cygnet-DEFAULT transition-colors"
        >
          Get in Touch
        </Link>
        <a
          href="tel:+442085385400"
          onClick={onClose}
          className="flex items-center justify-center gap-2 w-full rounded-xl border border-gray-200 bg-white px-5 py-3.5 text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Phone className="h-4 w-4 text-cygnet-dark" />
          0208 538 5400
        </a>
        <a
          href="mailto:info@cygnetcall.co.uk"
          onClick={onClose}
          className="flex items-center justify-center gap-2 w-full text-sm text-gray-500 hover:text-cygnet-dark transition-colors py-1"
        >
          <Mail className="h-4 w-4" />
          info@cygnetcall.co.uk
        </a>
      </div>
    </div>
  </>
);

export default MobileNavMenu;
